import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from "uuid";
import Grid from '@mui/material/Grid2';
import {  
  Feature,
  FeatureBase,
} from "@models";
import {  
  TransformOption,
  UpgradableNumber,
  TemplateBase, 
  WeaponLimitation,
  AbilityLimitation,
  SpellLimitation,
} from "@dndModels";
import {
  CREATURE_FEATURE_TYPES,
  CREATURE_TYPES,
  SIZE_OPTIONS,
} from "@dndModels/Constants";
import { CreatureFBType, CreatureType, SizeType } from '@dndModels/Types';

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import TemplateBox from "@dndComponents/TemplateBox";
import StringsBox from "@components/input/strings/StringsBox";
import CheckBox from '@components/input/CheckBox';
import SelectObjectBox from '@components/input/selects/SelectObjectBox';
import FeatureBaseInput from './FeatureBaseInput';
import FeatureBasesInput from './FeatureBasesInput';
import WeaponLimitInput from '@dndComponents/limitations/WeaponLimitInput';
import AbilityLimitInput from '@dndComponents/limitations/AbilityLimitInput';
import SpellLimitInput from '@dndComponents/limitations/SpellLimitInput';
import TransformAttributesInput from '@dndComponents/creature/TransformAttributesInput';
import CenteredMenu from '@components/input/menus/CenteredMenu';
import CreatureLimitInput from '@dndComponents/limitations/CreatureLimitInput';
import PairedStatsUGNInput from '@dndComponents/creature/PairedStatsUGNInput';

interface Props { 
  obj: TransformOption;
  onChange: (changed: TransformOption) => void;
  slot_level: number;
  disabled: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function TransformOptionInput({ 
  obj,
  onChange,
  slot_level,
  disabled
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [mode, setMode] = useState("Home");
  const [ping, setPing] = useState(false);
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBase | null>(null);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderOptions1 = () => {
    const extra_options = obj.use_creature ? ["Inherit from Target","Inherit from Creature"] : ["Inherit from Target"];
    return (
      <>
        <Grid size={12}>
          <CheckBox 
            name="Use Creature" 
            value={obj.use_creature} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.use_creature = changed;
              if (!changed) {
                if (new_obj.creature_type === "Inherit from Creature") {
                  new_obj.creature_type = "Inherit from Target";
                }
                if (new_obj.swarm === "Inherit from Creature") {
                  new_obj.swarm = "Inherit from Target";
                }
              }
              onChange(new_obj);
              setPing(!ping);
            }} 
          />
        </Grid>
        { obj.use_creature && 
          <Grid size={12}>
            <CreatureLimitInput 
              obj={obj.creature_limit} 
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.creature_limit = changed.clone();
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        }
        <Grid size={4}>
          <SelectStringBox 
            name="Creature Type" 
            options={CREATURE_TYPES}
            extra_options={extra_options}
            value={obj.creature_type} 
            auto_sort
            disabled={disabled}
            onChange={(changed: CreatureType | "Inherit from Target" | "Inherit from Creature") => {
              const new_obj = obj;
              new_obj.creature_type = changed;
              onChange(new_obj);
              setPing(!ping);
            }} 
          />
        </Grid>
        <Grid size={4}>
          <StringsBox 
            name="Tags"
            values={obj.tags} 
            disabled={disabled}
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.tags = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          <SelectStringBox 
            name="Swarm"
            value={obj.swarm} 
            options={["Yes","No"]}
            extra_options={extra_options}
            disabled={disabled}
            onChange={(changed: "Yes" | "No" | "Inherit from Target" | "Inherit from Creature") => {
              const new_obj = obj;
              new_obj.swarm = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
      </>
    );
  }

  const renderMaxHP = () => {
    return (
      <>
        <Grid size={12} className="header centered">Max Hit Points</Grid>
        <Grid size={4}>
          <CheckBox
            name="From Target"
            value={obj.max_hp_from_char} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.max_hp_from_char = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          <UpgradableNumberBox
            name="Extra"
            value={obj.extra_max_hp} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.extra_max_hp = changed.clone();
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          { obj.use_creature && 
            <CheckBox
              name="From Creature"
              value={obj.max_hp_from_creature} 
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.max_hp_from_creature = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          }
        </Grid>
      </>
    );
  }

  const renderTempHP = () => {
    return (
      <>
        <Grid size={12} className="header centered">Temp Hit Points</Grid>
        <Grid size={6}>
          <UpgradableNumberBox
            name="From Target"
            slot_level={slot_level}
            value={obj.temp_hit_points.from_target} 
            disabled={disabled}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.temp_hit_points.from_target = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={6}>
          { obj.use_creature && 
            <UpgradableNumberBox
              name="From Creature"
              slot_level={slot_level}
              value={obj.temp_hit_points.from_creature} 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.temp_hit_points.from_creature = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          }
        </Grid>
      </>
    );
  }

  const renderCR = () => {
    return (
      <>
        <Grid size={12} className="header centered">Challenge Rating</Grid>
        <Grid size={4}>
          <CheckBox
            name="From Target"
            value={obj.cr_from_char} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.cr_from_char = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          <UpgradableNumberBox
            name="Extra"
            value={obj.extra_cr} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.extra_cr = changed.clone();
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          { obj.use_creature && 
            <CheckBox
              name="From Creature"
              value={obj.cr_from_creature} 
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.cr_from_creature = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          }
        </Grid>
      </>
    );
  }

  const renderPB = () => {
    return (
      <>
        <Grid size={12} className="header centered">Proficiency Bonus</Grid>
        <Grid size={4}>
          <CheckBox
            name="From Target"
            value={obj.pb_from_char} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.pb_from_char = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          <UpgradableNumberBox
            name="Extra"
            value={obj.extra_pb} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.extra_pb = changed.clone();
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          { obj.use_creature && 
            <CheckBox
              name="From Creature"
              value={obj.pb_from_creature} 
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.pb_from_creature = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          }
        </Grid>
      </>
    );
  }

  const renderInit = () => {
    return (
      <>
        <Grid size={12} className="header centered">Initiative Modifier</Grid>
        <Grid size={4}>
          <CheckBox
            name="From Target"
            value={obj.init_from_char} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.init_from_char = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          <UpgradableNumberBox
            name="Extra"
            value={obj.extra_init} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.extra_init = changed.clone();
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          { obj.use_creature && 
            <CheckBox
              name="From Creature"
              value={obj.init_from_creature} 
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.init_from_creature = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          }
        </Grid>
      </>
    );
  }

  const renderAC = () => {
    return (
      <>
        <Grid size={12} className="header centered">Armor Class</Grid>
        <Grid size={4}>
          <CheckBox
            name="From Target"
            value={obj.ac_from_char} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.ac_from_char = changed;
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          <UpgradableNumberBox
            name="Extra"
            value={obj.extra_ac} 
            disabled={disabled}
            onChange={(changed) => {
              const new_obj = obj;
              new_obj.extra_ac = changed.clone();
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid size={4}>
          { obj.use_creature && 
            <CheckBox
              name="From Creature"
              value={obj.ac_from_creature} 
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.ac_from_creature = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          }
        </Grid>
      </>
    );
  }

  const renderSize = () => {
    return (
      <>
        <Grid size={12}>
          <SelectStringBox
            name="Size"
            value={`${obj.size}`} 
            options={SIZE_OPTIONS}
            extra_options={["From Char", "From Creature"]}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              if (changed === "From Char" || changed === "From Creature") {
                new_obj.size = changed;
              } else {
                new_obj.size = +changed as SizeType
              }
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
      </>
    );
  }

  const renderFBs = (creature_fb_type: CreatureFBType) => {
    if (expanded_feature_base) {
      // Creature FBs are simple, and only ever have one F, and the FB's name and description match the FF.
      const feature = expanded_feature_base.features[0];
      if (feature) {
        return (
          <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
            <FeatureBaseInput
              parent_name={obj.name}
              feature_base={expanded_feature_base} 
              feature_bases={obj.fbs.filter(o => o.creature_fb_type === creature_fb_type)}
              feature_source={obj}
              disabled={disabled}
              simple
              allowed_feature_types={[...CREATURE_FEATURE_TYPES,"Minion Ability"]}
              onChange={(changed: FeatureBase) => {
                changed.creature_fb_type = creature_fb_type;
                const new_obj = obj;
                let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
                if (objFinder.length === 1) {
                  const feature_base = objFinder[0];
                  feature_base.copy(changed);
                } else {
                  if (common_util.unique_array(objFinder.map(o => 
                    o.features[0].floating_feature_id)).length === 1) {
                    // Something went wrong, causing it to duplicate one of them.  
                    // Delete them and then add changed.
                    new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                    new_obj.fbs.push(changed);
                  } else {
                    // Give one of them a new ID.  
                    // They probably have different levels, so use that.
                    objFinder = objFinder.filter(o => o.level === changed.level);
                    if (objFinder.length === 1) {
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                      for (const other of others) {
                        other.true_id = uuidv4().toString();
                      }
                    } else {
                      // Just pick one.
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      for (let i = 1; i < objFinder.length; ++i) {
                        const other = objFinder[i];
                        if (other) {
                          other.true_id = uuidv4().toString();
                        }
                      }
                    }
                  }
                }
                onChange(new_obj);
                setPing(!ping);
              }}
              onDelete={() => {
                if (expanded_feature_base) {
                  const id = expanded_feature_base.id;
                  const new_obj = obj;
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                  setExpandedFeatureBase(null);
                  onChange(new_obj);
                  setPing(!ping);
                }
              }}
              onDone={() => {
                setExpandedFeatureBase(null);
              }}
            />
          </div>
        );
      }
      return <>Error</>;
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.fbs.filter(o => o.creature_fb_type === creature_fb_type)} 
          feature_source={obj}
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = creature_fb_type;
            new_fb.feature_source_id = new_obj.true_id;
            new_fb.feature_source_type = "summon";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            onChange(new_obj);
            setPing(!ping);
            setExpandedFeatureBase(new_fb);
          }}
          onReorder={(updated) => {
            const new_obj = obj;
            for (const u of updated) {
              const fb = new_obj.fbs.find(o => o.true_id === u.true_id);
              if (fb) {
                fb.copy(u);
              }
            }
            // new_obj.fbs = updated;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      );
    }
  }

  const renderBothActionLimits = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={6}>
          { renderActionLimits(true) }
        </Grid>
        { obj.use_creature && 
          <Grid size={6}>
            { renderActionLimits(false) }
          </Grid>
        }
      </Grid>
    );
  }

  const renderActionLimits = (char: boolean) => {
    if (char) {
      const action_limits = obj.char_action_limits;
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12} className="header centered">
            From Target
          </Grid>
          <Grid size={12}>
            <SelectStringBox 
              name="Action Types"
              values={action_limits.action_types} 
              options={["Unarmed Strike","Weapon","Ability","Spell"]}
              disabled={disabled}
              onChange={(changed: ("Unarmed Strike" | "Weapon" | "Ability" | "Spell")[]) => {
                const new_obj = obj;
                new_obj.char_action_limits.action_types = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          { action_limits.action_types.includes("Weapon") && 
            <Grid size={12}>
              <WeaponLimitInput
                obj={action_limits.weapon_limit}
                disabled={disabled}
                onChange={(changed: WeaponLimitation) => {
                  const new_obj = obj;
                  new_obj.char_action_limits.weapon_limit = changed.clone();
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
          }
          { action_limits.action_types.includes("Ability") && 
            <Grid size={12}>
              <AbilityLimitInput
                obj={action_limits.ability_limit}
                disabled={disabled}
                onChange={(changed: AbilityLimitation) => {
                  const new_obj = obj;
                  new_obj.char_action_limits.ability_limit = changed.clone();
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
          }
          { action_limits.action_types.includes("Spell") && 
            <Grid size={12}>
              <SpellLimitInput
                obj={action_limits.spell_limit}
                disabled={disabled}
                onChange={(changed: SpellLimitation) => {
                  const new_obj = obj;
                  new_obj.char_action_limits.spell_limit = changed.clone();
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
          }
        </Grid>
      );
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12} className="header centered">
            From Creature
          </Grid>
          <Grid size={12}>
            <SelectStringBox 
              name="Action Types"
              values={obj.creature_action_limits} 
              options={["Traits","Actions","Bonus Actions","Reactions","Legendary Actions","Spells"]}
              disabled={disabled}
              onChange={(changed: ("Traits" | "Actions" | "Bonus Actions" | "Reactions" | "Legendary Actions" | "Spells")[]) => {
                const new_obj = obj;
                new_obj.creature_action_limits = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </Grid>
      );
    }
  }

  const render_tab = () => {
    if (mode === "Ability Scores") {
      return (
        <PairedStatsUGNInput
          ability_scores={obj.ability_scores}
          use_creature={obj.use_creature}
          disabled={disabled}
          onChange={(changed) => {
            const new_obj = obj;
            new_obj.ability_scores = changed.clone();
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      );
    } else if (mode === "Attributes") {
      return (
        <TransformAttributesInput
          attributes={obj.attributes}
          use_creature={obj.use_creature}
          disabled={disabled}
          onChange={() => { // I should really change this input so it gives the changed version, and keeps a clone on it, but that's low priority.
            const new_obj = obj;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      );
    } else if (mode === "Traits") {
      return renderFBs("Traits");
    } else if (mode === "Actions") {
      return renderFBs("Actions");
    } else if (mode === "Legendary Actions") {
      return renderFBs("Legendary Actions");
    } else if (mode === "Inherited Actions/Traits") {
      return renderBothActionLimits();
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            <StringBox  
              name="Name"
              value={obj.name} 
              message={obj.name.length > 0 ? "" : "Name Invalid"}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.name = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={12}>
            <StringBox 
              name="Description"
              value={obj.description} 
              multiline
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.description = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>

          { renderOptions1() }
          { renderMaxHP() }
          { renderTempHP() }
          { renderCR() }
          { renderPB() }
          { renderInit() }
          { renderAC() }
          { renderSize() }

          <Grid size={4}>
            <SelectObjectBox 
              name="Gifts to Automatically Apply"
              data_type="creature_gift"
              values={ obj.built_in_gift_ids }
              disabled={disabled}
              multiple
              display_format='{obj.use_name}'
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.built_in_gift_ids = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            /> 
          </Grid>
        </Grid>
      ); 
    }
  }

  console.log(obj);
  return (
    <Grid container spacing={0} direction="row">
      <Grid size={12} style={{ width: "100%" }}>
        <TemplateBox
          obj={obj}
          type="TransformOption"
          useTemplate={(template: TemplateBase) => {
            const new_obj = obj;
            // debugger;
            new_obj.copyTemplate(template, api.all_floating_features);
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
      <Grid size={12}>
        <CenteredMenu
          options={["Home","Attributes","Ability Scores","Actions","Traits","Legendary Actions","Inherited Actions/Traits"]}
          selected={mode}
          onSelect={setMode}
        />
      </Grid>
      <Grid size={12}
        style={{ 
          overflowY: "scroll", 
          overflowX: "hidden" 
        }}>
          { !reloading && render_tab() }
      </Grid>
    </Grid>
  );
}
